import { z } from 'zod';
import { logger } from '@/lib/logger';

/**
 * Current workflow-schema revision. Bump whenever the on-disk/on-wire
 * shape changes in a way that requires a migration. The DB column
 * `workflows.schema_version` stores the version a row was written at,
 * so loaders can run `migrateWorkflowState(raw, row.schema_version)`
 * before validation.
 */
export const CURRENT_WORKFLOW_SCHEMA_VERSION = 1;

/**
 * Apply schema-version migrations to raw workflow data. Currently a
 * no-op for v1 (the only known version). Unknown future versions log
 * a warning and return the data as-is — the downstream Zod parse will
 * surface real incompatibilities as validation errors instead of
 * silent corruption.
 */
export function migrateWorkflowState(data: unknown, fromVersion: number): unknown {
  if (fromVersion === CURRENT_WORKFLOW_SCHEMA_VERSION) return data;
  if (fromVersion > CURRENT_WORKFLOW_SCHEMA_VERSION) {
    logger.warn(
      `[workflowSchema] Loading workflow with newer schema_version=${fromVersion} than client supports (${CURRENT_WORKFLOW_SCHEMA_VERSION}). Parsing as-is — downstream validation may fail.`,
    );
    return data;
  }
  // Legacy versions (< 1) reserved for future back-compat. Today there
  // are no versions below 1, so fall through as identity.
  return data;
}

const BlockConfigSchema = z.object({
  name: z.string().min(1).max(500),
  description: z.string().max(2000).optional(),
  // Lång markdown-detaljbeskrivning (Detaljer-dialogen). Större cap än
  // `description` (2k chars för korta block-kort-summeringar) eftersom
  // det är där user samlar fri text + bilage-referenser.
  detailedDescription: z.string().max(50000).optional(),
  icon: z.string().max(100).optional(),
  to: z.string().max(500).optional(),
  subject: z.string().max(500).optional(),
}).passthrough(); // Allow dynamic keys from [key: string]

const WorkflowBlockSchema = z.object({
  id: z.string().min(1).max(200),
  type: z.string().min(1).max(50),
  x: z.number().finite(),
  y: z.number().finite(),
  config: BlockConfigSchema,
  color: z.string().max(50),
  mtime: z.number().finite().optional(),
});

const PortSideSchema = z.enum([
  'top', 'top-right', 'right', 'bottom-right',
  'bottom', 'bottom-left', 'left', 'top-left',
]);

const ConnectorSchema = z.object({
  id: z.string().min(1).max(200),
  fromId: z.string().min(1).max(200),
  toId: z.string().min(1).max(200),
  fromPort: PortSideSchema.optional(),
  toPort: PortSideSchema.optional(),
  waypoints: z.array(z.object({
    x: z.number().finite(),
    y: z.number().finite(),
  })).optional(),
  // Optional connector label (text rendered along the path). Must be in the
  // schema or zod will silently strip it on parse, dropping data on save/load.
  label: z.string().max(500).optional(),
  mtime: z.number().finite().optional(),
});

const BlockGroupSchema = z.object({
  id: z.string().min(1).max(200),
  blockIds: z.array(z.string().min(1).max(200)),
  color: z.string().max(50),
  locked: z.boolean().optional(),
  name: z.string().max(500).optional(),
  parentGroupId: z.string().max(200).nullish().transform(v => v ?? undefined),
  collapsed: z.boolean().optional(),
  collapsedX: z.number().finite().optional(),
  collapsedY: z.number().finite().optional(),
  boundsLeft: z.number().finite().optional(),
  boundsTop: z.number().finite().optional(),
  boundsRight: z.number().finite().optional(),
  boundsBottom: z.number().finite().optional(),
  mtime: z.number().finite().optional(),
});

export const WorkflowStateSchema = z.object({
  name: z.string().max(500).default(''),
  blocks: z.array(WorkflowBlockSchema),
  connectors: z.array(ConnectorSchema).default([]),
  groups: z.array(BlockGroupSchema).default([]),
  coordinateOrigin: z.literal('center').optional(),
});

// ─── Multi-Sheet Schemas ────────────────────────────────────────────

const SheetMetaSchema = z.object({
  description: z.string().max(2000).optional(),
  color: z.string().max(50).optional(),
  icon: z.string().max(100).optional(),
}).optional();

const WorkflowSheetSchema = z.object({
  id: z.string().min(1).max(200),
  // Tom sträng tillåten — sheets utan user-text visas som ren sifferprefix
  // (t.ex. "②") via getSheetDisplayName(). Sifferprefix lagras INTE här.
  name: z.string().max(200).default(''),
  blocks: z.array(WorkflowBlockSchema).default([]),
  connectors: z.array(ConnectorSchema).default([]),
  groups: z.array(BlockGroupSchema).default([]),
  meta: SheetMetaSchema,
});

const CrossSheetConnectorSchema = z.object({
  id: z.string().min(1).max(200),
  fromSheetIndex: z.number().int().min(0),
  toSheetIndex: z.number().int().min(0),
  fromBlockId: z.string().min(1).max(200),
  toBlockId: z.string().min(1).max(200),
  fromPort: PortSideSchema.optional(),
  toPort: PortSideSchema.optional(),
  label: z.string().max(500).optional(),
}).refine(
  // Adjacency constraint lifted in Ship 7 (Caveat 5A). Connectors may
  // link any two distinct sheets; only self-loops are rejected.
  (c) => c.fromSheetIndex !== c.toSheetIndex,
  { message: 'Cross-sheet connectors cannot link a sheet to itself' },
);

export const MultiSheetWorkflowStateSchema = z.object({
  name: z.string().max(500).default(''),
  sheets: z.array(WorkflowSheetSchema).min(1),
  crossSheetConnectors: z.array(CrossSheetConnectorSchema).default([]),
  activeSheetIndex: z.number().int().min(0).default(0),
  coordinateOrigin: z.literal('center').optional(),
});

export { WorkflowSheetSchema, CrossSheetConnectorSchema };

/**
 * Safely parse and validate a WorkflowState from untrusted JSON.
 * Returns the validated state or null if invalid. `fromVersion` is the
 * `schema_version` that the data was originally written at; defaults to
 * the current version for newly-created data in-memory.
 */
export function parseWorkflowState(
  data: unknown,
  fromVersion: number = CURRENT_WORKFLOW_SCHEMA_VERSION,
): z.infer<typeof WorkflowStateSchema> | null {
  const migrated = migrateWorkflowState(data, fromVersion);
  const result = WorkflowStateSchema.safeParse(migrated);
  if (result.success) return result.data;
  logger.error('WorkflowState validation failed:', result.error.issues);
  return null;
}

/**
 * Safely parse and validate a MultiSheetWorkflowState.
 * Returns the validated state or null if invalid.
 */
export function parseMultiSheetState(data: unknown): z.infer<typeof MultiSheetWorkflowStateSchema> | null {
  const result = MultiSheetWorkflowStateSchema.safeParse(data);
  if (result.success) return result.data;
  logger.error('MultiSheetWorkflowState validation failed:', result.error.issues);
  return null;
}

/**
 * Returns a human-readable list of validation errors, or null if valid.
 */
export function getWorkflowValidationErrors(data: unknown): string[] | null {
  const result = WorkflowStateSchema.safeParse(data);
  if (result.success) return null;
  return result.error.issues.map(issue => {
    const path = issue.path.join('.');
    return `${path}: ${issue.message}`;
  });
}

// ─── Invariant: one block belongs to at most one group ──────────────
//
// Groups reference blocks via `blockIds`. A healthy workflow has each
// blockId in at most one group's `blockIds`. Legacy bugs (pre–layer-
// navigation-refactor + a handful of crashed drag-to-group ops) left
// some users' workflows with the same id in multiple groups, which
// causes double-counting in `computeGroupBounds` and ambiguous layer-
// navigation.

interface DuplicateMembership {
  blockId: string;
  groupIds: string[];
}

/**
 * Returns blocks that appear in more than one group. Empty array = healthy.
 */
export function findDuplicateBlockGroupMemberships(
  groups: { id: string; blockIds: string[] }[],
): DuplicateMembership[] {
  const membership = new Map<string, string[]>();
  for (const g of groups) {
    for (const blockId of g.blockIds) {
      const arr = membership.get(blockId);
      if (arr) arr.push(g.id);
      else membership.set(blockId, [g.id]);
    }
  }
  const dupes: DuplicateMembership[] = [];
  for (const [blockId, groupIds] of membership) {
    if (groupIds.length > 1) dupes.push({ blockId, groupIds });
  }
  return dupes;
}

/**
 * Repairs a workflow's groups so every block belongs to at most one
 * group. First-seen-wins: the block stays in the first group that
 * references it (in `groups` order) and is removed from the rest.
 * Safe to call on healthy states — a no-op that returns the same array.
 */
export function repairOneBlockOneGroup<G extends { id: string; blockIds: string[] }>(
  groups: G[],
): { groups: G[]; removed: DuplicateMembership[] } {
  const seen = new Set<string>();
  const removed = findDuplicateBlockGroupMemberships(groups);
  if (removed.length === 0) return { groups, removed };

  const fixed = groups.map(g => {
    const keep = g.blockIds.filter(id => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return keep.length === g.blockIds.length ? g : { ...g, blockIds: keep };
  });
  return { groups: fixed, removed };
}
